/* OTK boombox (snippets/otk-boombox.liquid + assets/otk-boombox.css): the boombox on its shelf under the OTK sign in the
   Sakura hero (sections/otk-sakura-hero.liquid), and the music it plays.
   - It waits behind the door: the hero (assets/otk-sakura-hero.js onDoor) clips and masks its layer with the door like
     the rack and the sign, and marks it .is-shut until the door is fully open, and again once the door, sliding back,
     reaches it; meanwhile the layer is inert (it can't be focused or pressed). While the room's lights are off it is
     dark (the CSS reads --otk-lit-top); once they're on and the door is open it "wakes": its meter glows faintly (it is
     on, waiting), the play key's LED blinks slowly, a few notes rise out of it to say hello and then one every couple of
     seconds, inviting a click. Under the pointer the meter warms up as if about to play.
   - Its size: it stays clear of the sign over it (fit(): smaller where the sign comes close, out of the room where
     there is no room at all; see the top of otk-boombox.css).
   - Click / tap / Enter / Space plays (the AudioContext is made and resumed inside that click, as browsers require):
     one audio element (nothing is downloaded before this first press) through WebAudio, element -> gain -> analyser ->
     speakers (the gain does the fades: iOS ignores an element's volume). The analyser's bass drives the speaker cones
     (they pump, and the case thumps a little) and the notes (they come on the beat), its two channels the VU needles,
     its level the meter's lamp and the warm light it throws on the wall; the reels turn and the play key stays down.
     Pressing again pauses, with a short fade. Something else taking the sound away (a phone call, Siri, another app's
     audio on an iPhone) shows as a pause; the next press starts it again.
   - The playlist (the section's setting, or the theme's placeholders: the JSON in the snippet) is shuffled once per visit
     (kept for the browser session), always opening with a Japanese lo-fi track; it plays on through the list and round
     again. A track that fails is skipped; if every track fails the boombox switches itself off, quietly. A file on
     another server (Shopify Files, cdn.shopify.com) that doesn't allow WebAudio to read it (no CORS) is played without
     WebAudio, with the speakers pumping to a gentle 85 BPM pulse instead (as everything does where WebAudio is missing).
   - When a track starts, its name shows under the shelf for a few seconds and screen readers hear "Now playing: ...".
   - Scrolled away while it plays (the boombox off screen and the hero no longer pinned, or back up behind the closed
     door), a small "now playing" pill stays at the bottom left with pause / play and next; it goes when the boombox is
     back within reach, or 6 s after a pause (never from under a keyboard user's focus), and steps aside while something
     it must not cover is under it (the hero's SCROLL cue, the footer's last row). The music stops when the visitor
     leaves the page.
   - Work per frame only while it shows: playing, or notes in the air, with the boombox on screen and the tab visible.
     Reduced motion: no notes, pumping or turning reels (the meter just glows); the music works the same.
   Picture numbers (IMG) are the landmarks in scripts/make_boombox_assets.py. */
(function () {
  'use strict';

  // otk-boombox.webp, 894 x 543: speaker centres, the VU needles' pivots (under the window) and their swing (degrees
  // from straight up: at rest, just under -20 VU; at +3 VU, the end of the scale), the case's top edge (the key row)
  var IMG = { w: 894, speakers: [[172, 363], [719, 363]], pivots: [[402.5, 297], [498.5, 297]], rest: -38.5, max: 36, top: 150 };
  // the boombox is 160 frame px wide at its full size: the notes' sizes and speeds are in these units ("nu", CSS px per
  // frame px of the boombox), so they keep their size next to it whatever its size
  // ♪ ♫ ♬ in a 24 px box (SVG path data: heads tilted 20 degrees, stems, flag / beams)
  var NOTE_PATHS = [
    'M3.84 18.57a4 2.9-20 1 0 7.52-2.74 4 2.9-20 1 0-7.52 2.74zM10.2 16.4V3h1.4v13.4zM11.6 3c.3 2.3 1.9 3.3 3.4 4.4 1.7 1.3 2.6 3 1.7 5.9-.2-2.1-1.5-3.4-3-4.1-.8-.4-1.5-.6-2.1-.7z',
    'M2.02 19.63a3.6 2.6-20 1 0 6.77-2.46 3.6 2.6-20 1 0-6.77 2.46zM13.02 17.63a3.6 2.6-20 1 0 6.77-2.46 3.6 2.6-20 1 0-6.77 2.46zM7.6 18.2V6.2h1.3v12zM18.6 16.2V4.2h1.3v12zM7.6 5l12.3-2.4v2.8L7.6 7.8z',
    'M2.02 19.63a3.6 2.6-20 1 0 6.77-2.46 3.6 2.6-20 1 0-6.77 2.46zM13.02 17.63a3.6 2.6-20 1 0 6.77-2.46 3.6 2.6-20 1 0-6.77 2.46zM7.6 18.2V6.2h1.3v12zM18.6 16.2V4.2h1.3v12zM7.6 5l12.3-2.4v2.8L7.6 7.8zM7.6 9.4l12.3-2.4v2.4L7.6 11.8z'
  ];
  var FADE_IN = 0.6;    // s: the music comes up when it starts
  var FADE_OUT = 0.4;   // ... and goes down before it pauses
  var SWITCH = 0.25;    // ... and before the next track (presses within it add up: see next())
  var BPM = 85;         // the speakers' pulse where the music can't be analysed
  // RMS at 0 VU. The tracks are brought to -17 dBFS (scripts/prepare_boombox_audio.py) by loudness, which reads higher
  // than their plain RMS (it weighs the mids up and the bass down): measured in the browser, the placeholders run at an
  // RMS of 0.055-0.086 (median 0.066), so this puts their needles round 0 VU, kicking up into the red on the loud bits
  var VU_REF = 0.075;
  var STANDBY = 0.2;    // the meter's lamp while it waits to be pressed (it is on) ...
  var HOVER_LAMP = 0.4; // ... and under the pointer
  var MIN_FIT = 0.55;   // the smallest it gets to clear the sign (fit()); with less room than that it leaves the room
  var CAPTION_MS = 4200;
  var PILL_HOLD = 6000; // ms the pill stays after a pause off screen
  var STORE = 'otk-boombox';
  var SPRITE = 64;      // px: each note glyph is drawn once into a sprite this size, glow included
  // what the pill must not cover: while one of these is under it, and showing, it steps aside (the hero's SCROLL cue, on
  // small phones, and the footer's last row: the copyright and payment icons at the very end of the page)
  var CLEAR_OF = '.otk-sakura__hint, .footer__content-bottom';

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function smooth(a, b, v) { var t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); }
  function noop() {}

  function sameOrigin(src) {
    try { return new URL(src, location.href).origin === location.origin; } catch (e) { return true; }
  }

  // the three note glyphs, warm cream to the sign's gold, with a soft glow (drawn once)
  var sprites = null;
  function noteSprites() {
    if (sprites || !window.Path2D) return sprites;
    sprites = NOTE_PATHS.map(function (d) {
      var c = document.createElement('canvas'), g = c.getContext('2d'), k = SPRITE * 0.62 / 24, o = (SPRITE - 24 * k) / 2;
      c.width = c.height = SPRITE;
      g.translate(o, o);
      g.scale(k, k);
      var grad = g.createLinearGradient(0, 2, 0, 22);
      grad.addColorStop(0, '#fff3d6');
      grad.addColorStop(0.55, '#f0cf8a');
      grad.addColorStop(1, '#d9a55a');
      g.shadowColor = 'rgba(255, 196, 110, 0.9)';
      g.shadowBlur = SPRITE * 0.12;
      g.fillStyle = grad;
      var p = new Path2D(d);
      g.fill(p);
      g.shadowBlur = 0;
      g.fill(p); // crisp on top of its glow
      return c;
    });
    return sprites;
  }

  /* ---------------------------------------------------------------- the boombox */
  function Boombox(layer, section) {
    this.layer = layer;
    this.section = section;
    this.btn = layer.querySelector('[data-otk-bb-btn]');
    this.nextBtn = layer.querySelector('[data-otk-bb-next]');
    this.caption = layer.querySelector('[data-otk-bb-caption]');
    this.canvas = layer.querySelector('.otk-bb__notes');
    this.cones = [].slice.call(layer.querySelectorAll('[data-otk-bb-cone]'));
    this.caps = [].slice.call(layer.querySelectorAll('[data-otk-bb-cap]'));
    this.needleEls = [].slice.call(layer.querySelectorAll('[data-otk-bb-needle]'));
    this.vu = layer.querySelector('[data-otk-bb-vu]');
    this.bloom = layer.querySelector('.otk-bb__bloom');
    this.thump = layer.querySelector('.otk-bb__thump');
    this.scene = layer.querySelector('.otk-bb__scene');
    this.vol = clamp((parseFloat(layer.getAttribute('data-volume')) || 80) / 100, 0.3, 1);
    var list = [];
    try { list = JSON.parse(layer.querySelector('[data-otk-bb-list]').textContent) || []; } catch (e) { list = []; }
    this.tracks = list.filter(function (t) { return t && t.src; });
    this.listeners = [];
    this.notes = [];
    this.playing = false;
    this.open = false;
    this.lit = 1;
    this.onScreen = true;
    this.raf = 0;
    this.last = 0;
    this.nextIdle = 1.2; // s until the next idle note
    this.hello = 0;      // s until it says hello (a few notes) once it wakes
    this.helloN = 0;     // ... notes of it still to come
    this.fitV = 1;       // its size (fit())
    this.cramped = false;
    this.frameLeft = 608; // its left edge in frame px (the hero's door reaches it there)
    this.skip = 0;       // next presses waiting for the switch
    // the levels: bass envelope and its slow average (for the beat), the two needles (angle, speed), the lamp
    this.lv = { env: 0, avg: 0, prev: 0, since: 1, pump: 0, pumpW: -1, l: 0, r: 0, a: [IMG.rest, IMG.rest], v: [0, 0], ms: [0, 0], aW: [null, null], lamp: 0, lampW: -1, side: 0 };
    this.tick = this.tick.bind(this);
    if (!this.tracks.length || !this.btn) return; // nothing to play: it stays a still picture
    this.order = this.shuffled();
    this.idx = this.opener();
    this.bind();
    this.syncDoor();
    layer.classList.add('is-ready');
    this.layout();
    this.syncAwake();
  }

  Boombox.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.listeners.push([target, type, fn, opts]);
  };

  Boombox.prototype.hero = function () { return this.section._otkSakura || null; };

  // The order, once per visit: kept in the browser session (sessionStorage), so coming back to the homepage carries on
  // through the same shuffle; a different playlist (the owner changed it) shuffles anew. Lo-fi tracks open, so the first
  // of them sits first; the rest are shuffled.
  Boombox.prototype.shuffled = function () {
    var tracks = this.tracks, keys = tracks.map(function (t) { return t.src; }), saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(STORE) || 'null'); } catch (e) { saved = null; }
    if (saved && saved.order && saved.order.length === keys.length && saved.order.every(function (k) { return keys.indexOf(k) >= 0; })) {
      this.savedNext = saved.next | 0;
      return saved.order.map(function (k) { return tracks[keys.indexOf(k)]; });
    }
    var lofi = tracks.filter(function (t) { return t.lofi; }), first = lofi.length ? lofi[(Math.random() * lofi.length) | 0] : tracks[0];
    var rest = tracks.filter(function (t) { return t !== first; });
    for (var i = rest.length - 1; i > 0; i--) { var j = (Math.random() * (i + 1)) | 0, x = rest[i]; rest[i] = rest[j]; rest[j] = x; }
    this.savedNext = 0;
    return [first].concat(rest);
  };

  // where a visit's music starts: the first lo-fi track at or after where the last visit stopped
  Boombox.prototype.opener = function () {
    var n = this.order.length, start = (this.savedNext || 0) % n, i;
    if (!this.order.some(function (t) { return t.lofi; })) return start;
    for (i = 0; i < n; i++) if (this.order[(start + i) % n].lofi) return (start + i) % n;
    return 0;
  };

  Boombox.prototype.save = function () {
    try {
      sessionStorage.setItem(STORE, JSON.stringify({ order: this.order.map(function (t) { return t.src; }), next: (this.idx + 1) % this.order.length }));
    } catch (e) { /* private mode: a new shuffle next time */ }
  };

  Boombox.prototype.bind = function () {
    var self = this, layer = this.layer, btn = this.btn;
    // the press: it dips (and the play key goes down) while held, then springs back
    var press = function (on) { layer.classList.toggle('is-pressing', on); };
    this.on(btn, 'pointerdown', function (e) { if (e.button === 0 || e.pointerType !== 'mouse') press(true); });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (t) { self.on(btn, t, function () { press(false); }); });
    this.on(btn, 'keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') press(true); });
    this.on(btn, 'keyup', function () { press(false); });
    // under a mouse pointer its meter warms up (the frame loop eases it)
    this.on(btn, 'pointerenter', function (e) { if (e.pointerType === 'mouse') self.setHover(true); });
    this.on(btn, 'pointerleave', function (e) { if (e.pointerType === 'mouse') self.setHover(false); });
    this.on(btn, 'click', function (e) {
      e.preventDefault();
      e.stopPropagation(); // the rack and the trees under it never see it
      if (!layer.classList.contains('is-pressing')) { press(true); setTimeout(function () { press(false); }, 140); }
      else setTimeout(function () { press(false); }, 90);
      self.toggle();
    });
    if (this.nextBtn) this.on(this.nextBtn, 'click', function (e) { e.preventDefault(); e.stopPropagation(); self.next(); });
    // the door (the hero toggles .is-shut on this layer with the rack) and the rack's viewer (it covers the hero)
    if (window.MutationObserver) {
      this.mo = new MutationObserver(function () { self.syncDoor(); });
      this.mo.observe(layer, { attributes: true, attributeFilter: ['class'] });
      this.moSection = new MutationObserver(function () { self.syncDoor(); });
      this.moSection.observe(this.section, { attributes: true, attributeFilter: ['class', 'data-lights'] });
      this.moHtml = new MutationObserver(function () {
        var v = document.documentElement.classList.contains('otk-sakura-viewing');
        if (v !== self.viewing) { self.viewing = v; self.wake(); }
      });
      this.moHtml.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
    // the room's lights (otk-sakura-hero.js: 'otk:lights' on the section, while they switch and once settled; the
    // section's data-lights, watched above, says the same)
    this.on(this.section, 'otk:lights', function () { self.syncAwake(); });
    // on screen or not: the frame loop, and the pill
    if (window.IntersectionObserver) {
      this.io = new IntersectionObserver(function (en) {
        var e = en[en.length - 1];
        self.onScreen = e.isIntersecting && e.intersectionRatio >= 0.3;
        if (self.onScreen) self.layout();
        self.syncPill();
        self.wake();
      }, { threshold: [0, 0.3, 0.6] });
      this.io.observe(btn);
    }
    this.on(window, 'scroll', function () { if (self.playing || self.pillOn || self.pillHold) self.syncPill(); }, { passive: true });
    var relayout = function () {
      clearTimeout(self.layoutT);
      self.layoutT = setTimeout(function () { self.layout(); if (self.pill) self.watchAside(); }, 150);
    };
    this.on(window, 'resize', relayout);
    this.on(document, 'visibilitychange', function () { self.wake(); });
    // a page restored from the back / forward cache: the browser paused the music while it was away
    this.on(window, 'pageshow', function (e) { if (e.persisted && self.playing && self.cur && self.cur.paused) self.setPlaying(false); self.wake(); });
    var motion = function () { self.setPlaying(self.playing); self.wake(); };
    if (REDUCED.addEventListener) REDUCED.addEventListener('change', motion);
    else if (REDUCED.addListener) REDUCED.addListener(motion);
    this.motion = motion;
  };

  // the door is open when the hero has taken .is-shut off the layer (or the hero is the still room, or never started)
  Boombox.prototype.syncDoor = function () {
    var shut = this.layer.classList.contains('is-shut') && !this.section.classList.contains('is-static');
    var open = !shut;
    if (open !== this.open) {
      this.open = open;
      this.syncInert();
      if (open) this.layout();
      this.syncPill();
    }
    this.syncAwake();
  };

  // it can be focused and pressed while the door is open and there is room for it under the sign
  Boombox.prototype.syncInert = function () {
    var off = !this.open || this.cramped;
    if (this.layer.inert === off) return;
    this.layer.inert = off;
    if (off && this.layer.contains(document.activeElement)) document.activeElement.blur();
  };

  // awake: the door open and the room lit (their steady state: not while they flicker on), and something to play. It
  // then shows it is on: the meter's faint stand-by glow, the LED blinking, the notes (a hello of three, then one now
  // and then)
  Boombox.prototype.syncAwake = function () {
    var a = this.section.getAttribute('data-lights');
    this.lit = a === null || a === 'on' || this.section.classList.contains('is-static') ? 1 : 0;
    var awake = this.open && this.lit === 1 && !this.off && !this.cramped;
    if (awake !== this.awake) {
      this.awake = awake;
      this.layer.classList.toggle('is-awake', awake);
      if (awake) {
        this.nextIdle = 2.6 + Math.random() * 0.8; // (after the hello)
        if (!this.playing) { this.hello = 0.8; this.helloN = 0; }
      }
      this.settling = true; // the meter's lamp eases to its new rest (the frame loop)
      if (REDUCED.matches) this.stillLamp();
    }
    this.wake();
  };

  Boombox.prototype.setHover = function (on) {
    if (on === !!this.hover) return;
    this.hover = on;
    this.settling = true;
    if (REDUCED.matches) this.stillLamp();
    this.wake();
  };

  // the meter's lamp when the music isn't driving it: the stand-by glow while it waits (brighter under the pointer)
  Boombox.prototype.restLamp = function () {
    return this.awake ? (this.hover ? HOVER_LAMP : STANDBY) : 0;
  };

  // the lamp and its light on the wall, set at once (reduced motion: no frame loop)
  Boombox.prototype.stillLamp = function () {
    var v = this.playing && !this.off ? 0.8 : this.restLamp();
    this.lv.lamp = this.lv.lampW = v;
    this.writeLamp(v);
  };

  Boombox.prototype.writeLamp = function (v) {
    this.vu.style.opacity = v.toFixed(3);
    // the wall catches some of it: nothing at stand-by, a little under the pointer, up to 0.28 at the loudest
    if (this.bloom) this.bloom.style.opacity = (0.28 * clamp((v - STANDBY) / (1 - STANDBY), 0, 1)).toFixed(3);
  };

  /* ------------------------------------------------ the audio */

  // Made inside the first press (browsers only let a page start sound from one): the element and, where there is
  // WebAudio, its graph. The element asks for files with CORS (crossOrigin) so the analyser may read them; the theme's
  // own files are on the store's domain anyway.
  Boombox.prototype.setup = function () {
    if (this.el) return;
    var self = this, el = this.el = new Audio(), AC = window.AudioContext || window.webkitAudioContext;
    el.preload = 'auto';
    // iPhones (Safari 17 and later): music the visitor started plays even with the ring / silent switch on, like a video.
    // (WebAudio output is otherwise muted by that switch: the boombox would play in silence)
    try { if (navigator.audioSession && navigator.audioSession.type !== 'playback') navigator.audioSession.type = 'playback'; } catch (e) { /* */ }
    if (AC) {
      try {
        var ctx = new AC();
        el.crossOrigin = 'anonymous';
        var src = ctx.createMediaElementSource(el);
        var gain = ctx.createGain();
        gain.gain.value = 0;
        var an = ctx.createAnalyser();
        an.fftSize = 1024;
        an.smoothingTimeConstant = 0.3; // quick enough for a kick drum
        an.minDecibels = -90;           // (the default range, -100 to -30 dB, flattens loud bass at the top)
        an.maxDecibels = -10;
        src.connect(gain);
        gain.connect(an);
        an.connect(ctx.destination);
        // the two channels for the two needles
        var split = ctx.createChannelSplitter(2), aL = ctx.createAnalyser(), aR = ctx.createAnalyser();
        aL.fftSize = aR.fftSize = 1024;
        gain.connect(split);
        split.connect(aL, 0);
        split.connect(aR, 1);
        this.ctx = ctx;
        // a call, Siri or another app's sound can take the audio away from the page (iPhones: 'interrupted'; it can also
        // come back 'suspended'): the music has stopped, so the boombox shows it paused, and the next press starts it
        // again (resuming the context inside that press)
        this.on(ctx, 'statechange', function () {
          if (ctx.state !== 'running' && ctx.state !== 'closed' && self.playing && !self.dead) self.pause();
        });
        this.gain = gain;
        this.an = an;
        this.aLR = [aL, aR];
        this.freq = new Uint8Array(an.frequencyBinCount);
        this.td = new Float32Array(1024);
        this.tdB = new Uint8Array(1024);
        // the bass: about 45-160 Hz
        var bin = ctx.sampleRate / an.fftSize;
        this.bass = [Math.max(1, Math.round(45 / bin)), Math.max(2, Math.round(160 / bin))];
      } catch (e) {
        this.ctx = null;
        el.removeAttribute('crossorigin');
      }
    }
    this.wa = !!this.ctx;
    this.watch(el);
    // what screen readers hear when a track starts: an empty live region now, so it is in place before its first words
    // (outside the hero: the boombox's layer can be inert behind the door)
    this.live = document.createElement('p');
    this.live.className = 'otk-bb-live';
    this.live.setAttribute('aria-live', 'polite');
    document.body.appendChild(this.live);
    // the files on another server that will need the plain element (see onError): it starts its life in this press too,
    // so iPhones let it play later
    if (this.wa && this.tracks.some(function (t) { return !sameOrigin(t.src); })) this.plainEl(true);
    if ('mediaSession' in navigator) {
      try {
        navigator.mediaSession.setActionHandler('play', function () { self.play(); });
        navigator.mediaSession.setActionHandler('pause', function () { self.pause(); });
        navigator.mediaSession.setActionHandler('nexttrack', function () { self.next(); });
      } catch (e) { /* older browsers: no media keys */ }
    }
  };

  // an element without WebAudio, for files that can't be read with CORS (or the only one, where there is no WebAudio)
  Boombox.prototype.plainEl = function (unlock) {
    if (!this.wa) return this.el;
    if (!this.plain) {
      var p = this.plain = new Audio();
      p.preload = 'auto';
      this.watch(p);
      if (unlock) {
        // a moment of silence inside the press (an inline WAV header, no download)
        p.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
        var r = p.play();
        if (r && r.then) r.then(function () { p.pause(); }, noop);
      }
    }
    return this.plain;
  };

  Boombox.prototype.watch = function (el) {
    var self = this;
    this.on(el, 'playing', function () { if (el === self.cur) self.onPlaying(); });
    this.on(el, 'ended', function () { if (el === self.cur) self.onEnded(); });
    this.on(el, 'error', function () { if (el === self.cur && el.getAttribute('src') && el.src.indexOf('data:') !== 0) self.onError(el); });
  };

  // put track i (wrapping round) on the element that can play it
  Boombox.prototype.load = function (i) {
    var n = this.order.length;
    i = ((i % n) + n) % n;
    this.idx = i;
    var t = this.order[i], plain = !this.wa || t.plain, el = plain ? this.plainEl() : this.el;
    if (this.cur && this.cur !== el) this.cur.pause();
    this.cur = el;
    this.curWA = !plain;
    if (plain) el.volume = 0; // (faded up by fade(); iPhones ignore it and play at full volume)
    else el.volume = 1;
    el.src = t.src;
    this.loaded = true;
    this.announced = false;
    this.save();
  };

  Boombox.prototype.start = function () {
    var self = this, p = null;
    try { p = this.cur.play(); } catch (e) { p = null; }
    if (p && p.catch) {
      p.catch(function (err) {
        // the browser refused to start sound (no press): back to paused. A broken file comes as an 'error' event.
        if (err && err.name === 'NotAllowedError' && self.playing) self.setPlaying(false);
      });
    }
  };

  // the music up (or down) over dur seconds: the gain where there is WebAudio, the element's volume otherwise
  Boombox.prototype.fade = function (to, dur) {
    var self = this;
    clearInterval(this.volT);
    if (this.curWA && this.gain) {
      var g = this.gain.gain, now = this.ctx.currentTime;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(to, now + dur);
      return;
    }
    var el = this.cur;
    if (!el) return;
    var from = el.volume, t0 = Date.now();
    this.volT = setInterval(function () {
      var k = clamp((Date.now() - t0) / (dur * 1000), 0, 1);
      try { el.volume = clamp(from + (to - from) * k, 0, 1); } catch (e) { /* (iOS: read-only) */ }
      if (k >= 1) clearInterval(self.volT);
    }, 30);
  };

  Boombox.prototype.toggle = function () {
    if (this.off) return;
    if (this.playing) this.pause();
    else this.play();
  };

  // the AudioContext back on (inside a press): after the first one it is 'suspended' or, on an iPhone after a call,
  // 'interrupted'
  Boombox.prototype.resume = function () {
    var c = this.ctx;
    if (c && c.state !== 'running' && c.state !== 'closed' && c.resume) c.resume().catch(noop);
  };

  Boombox.prototype.play = function () {
    if (this.off || this.dead) return;
    this.setup();
    this.resume();
    clearTimeout(this.pauseT);
    if (!this.loaded) this.load(this.idx);
    this.setPlaying(true);
    this.fadeIn();
    this.start();
  };

  // the music comes up once it really sounds: at once if the element has the data, else when it starts ('playing'),
  // so a slow download doesn't use up the fade in silence
  Boombox.prototype.fadeIn = function () {
    var el = this.cur;
    if (el && el.readyState >= 3 && !el.seeking) { this.fadeWait = false; this.fade(this.vol, FADE_IN); return; }
    this.fadeWait = true;
    this.fade(0, 0.02);
  };

  Boombox.prototype.pause = function () {
    var self = this, el = this.cur;
    if (this.switchT) { // pressed while changing tracks: the track it was going to is ready for the next press
      clearTimeout(this.switchT);
      this.switchT = 0;
      this.load(this.idx + Math.max(1, this.skip));
      this.skip = 0;
      el = this.cur;
    }
    this.fadeWait = false;
    this.setPlaying(false);
    this.fade(0, FADE_OUT);
    clearTimeout(this.pauseT);
    this.pauseT = setTimeout(function () { if (!self.playing && el) el.pause(); }, FADE_OUT * 1000 + 40);
  };

  // next track: a quick fade, then the next one comes up (while paused, it starts it). Presses in quick succession each
  // count: the switch waits until they stop, then goes on that many tracks
  Boombox.prototype.next = function () {
    if (this.off || this.dead) return;
    var self = this;
    this.setup();
    this.resume();
    if (!this.playing || !this.loaded) {
      this.load(this.loaded ? this.idx + 1 : this.idx);
      this.play();
      return;
    }
    clearTimeout(this.switchT);
    if (!this.skip) this.fade(0, SWITCH);
    this.skip++;
    this.switchT = setTimeout(function () {
      var n = self.skip;
      self.switchT = 0;
      self.skip = 0;
      if (!self.playing) return;
      self.load(self.idx + n);
      self.fadeIn();
      self.start();
    }, SWITCH * 1000);
  };

  Boombox.prototype.onPlaying = function () {
    this.fails = 0;
    if (this.fadeWait && this.playing) { this.fadeWait = false; this.fade(this.vol, FADE_IN); }
    if (!this.announced) {
      this.announced = true;
      this.announce(this.order[this.idx]);
    }
    if (!this.playing && this.cur) this.cur.pause(); // paused while it was still loading
  };

  Boombox.prototype.onEnded = function () {
    this.load(this.idx + 1);
    if (this.playing) {
      this.fadeIn();
      this.start();
    }
  };

  // A track that can't be played: from another server without CORS, it is tried again without WebAudio; otherwise it is
  // skipped. If the whole list fails in a row, the boombox switches off (no retries, nothing in the console).
  Boombox.prototype.onError = function (el) {
    var t = this.order[this.idx];
    if (el === this.el && this.wa && !t.plain && !sameOrigin(t.src)) {
      t.plain = true;
      this.load(this.idx);
      if (this.playing) { this.fadeIn(); this.start(); }
      return;
    }
    this.fails = (this.fails || 0) + 1;
    if (this.fails >= this.order.length) { this.disable(); return; }
    this.load(this.idx + 1);
    if (this.playing) { this.fadeIn(); this.start(); }
  };

  Boombox.prototype.disable = function () {
    this.off = true;
    this.setPlaying(false);
    if (this.cur) { this.cur.pause(); this.cur.removeAttribute('src'); }
    this.layer.classList.add('is-off');
    this.btn.setAttribute('aria-disabled', 'true');
    this.btn.setAttribute('aria-label', 'Music unavailable');
    this.btn.removeAttribute('aria-pressed');
    this.hidePill();
    this.syncAwake();
  };

  // playing or not: the button, the meter's lamp, the reels and the key (CSS), the next button, the pill. (The button's
  // name stays "Play music": aria-pressed says whether it is on)
  Boombox.prototype.setPlaying = function (on) {
    this.playing = on;
    var L = this.layer, b = this.btn;
    L.classList.toggle('is-playing', on);
    if (!this.off) b.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (this.nextBtn) this.nextBtn.hidden = !on;
    if (!on && this.nextBtn === document.activeElement) b.focus({ preventScroll: true });
    if (REDUCED.matches) {
      // a still, lit meter: the lamp on and the needles up near 0 VU while it plays
      this.stillLamp();
      this.setNeedle(0, on ? 4 : IMG.rest);
      this.setNeedle(1, on ? 6 : IMG.rest);
      this.cones.concat(this.caps).forEach(function (c) { c.style.transform = ''; });
      if (this.thump) this.thump.style.transform = '';
      this.lv.pumpW = -1;
    } else if (!on) {
      this.settling = true; // the needles, cones and lamp come to rest in the frame loop
      this.nextIdle = 1.5 + Math.random() * 1.5;
    }
    if ('mediaSession' in navigator) { try { navigator.mediaSession.playbackState = on ? 'playing' : 'paused'; } catch (e) { /* */ } }
    if (on) { clearTimeout(this.holdT); this.pillHold = false; this.hello = 0; this.helloN = 0; }
    else if (this.pillOn) {
      // paused with the pill up: it stays a while (to start it again), then goes
      var self = this;
      this.pillHold = true;
      clearTimeout(this.holdT);
      this.holdT = setTimeout(function () { self.pillHold = false; self.syncPill(); }, PILL_HOLD);
    }
    this.syncPill();
    this.wake();
  };

  // "♪ Title — Artist" under the shelf for a few seconds, the same to screen readers, the pill and the system's media
  // controls
  Boombox.prototype.announce = function (t) {
    var self = this, name = t.title + (t.artist ? ' — ' + t.artist : '');
    if (this.caption) {
      this.caption.textContent = '♪ ' + name;
      this.caption.classList.add('is-on');
      clearTimeout(this.capT);
      this.capT = setTimeout(function () { self.caption.classList.remove('is-on'); }, CAPTION_MS);
    }
    if (this.live) this.live.textContent = 'Now playing: ' + t.title + (t.artist ? ' by ' + t.artist : '');
    if (this.pill) this.pillName(t);
    if ('mediaSession' in navigator && window.MediaMetadata) {
      try { navigator.mediaSession.metadata = new MediaMetadata({ title: t.title, artist: t.artist || '', album: 'OTK Digital' }); } catch (e) { /* */ }
    }
  };

  /* ------------------------------------------------ the pill: now playing, while the boombox is off screen */
  Boombox.prototype.makePill = function () {
    if (this.pill) return;
    var self = this, p = this.pill = document.createElement('div');
    p.className = 'otk-bbpill';
    p.setAttribute('role', 'group');
    p.setAttribute('aria-label', 'Now playing');
    p.innerHTML = '<span class="otk-bbpill__eq" aria-hidden="true"><i></i><i></i><i></i><i></i></span>' +
      '<span class="otk-bbpill__title" aria-hidden="true"></span>' +
      '<span class="otk-bbpill__btns">' +
      '<button class="otk-bbpill__btn" type="button" data-pp aria-label="Pause music">' +
      '<svg class="otk-bbpill__i-pause" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.5 2.5h3v11h-3zM9.5 2.5h3v11h-3z"/></svg>' +
      '<svg class="otk-bbpill__i-play" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 2.2v11.6L13.4 8z"/></svg></button>' +
      '<button class="otk-bbpill__btn" type="button" data-nx aria-label="Next track">' +
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M2.5 2.6v10.8L10.1 8z"/><path d="M10.9 2.6h1.8v10.8h-1.8z"/></svg></button></span>';
    this.pillTitle = p.querySelector('.otk-bbpill__title');
    this.pillPP = p.querySelector('[data-pp]');
    this.on(this.pillPP, 'click', function () { self.toggle(); });
    this.on(p.querySelector('[data-nx]'), 'click', function () { self.next(); });
    // the pill never goes from under a keyboard user's focus (syncPill): once the focus leaves it, look again
    this.on(p, 'focusout', function () { setTimeout(function () { self.syncPill(); }, 60); });
    if (this.announced) this.pillName(this.order[this.idx]);
    document.body.appendChild(p);
    this.liftPill();
    this.watchAside();
  };

  // the track's name: on the plate (not on phones: otk-boombox.css), in the group's label for screen readers, and in its
  // tooltip (a long press on a phone)
  Boombox.prototype.pillName = function (t) {
    var name = t.title + (t.artist ? ' — ' + t.artist : '');
    this.pillTitle.textContent = '';
    this.pillTitle.appendChild(document.createTextNode(t.title));
    if (t.artist) {
      var b = document.createElement('b');
      b.textContent = ' · ' + t.artist;
      this.pillTitle.appendChild(b);
    }
    this.pill.title = name;
    this.pill.setAttribute('aria-label', 'Now playing: ' + t.title + (t.artist ? ' by ' + t.artist : ''));
  };

  // theme previews only: Shopify's preview bar covers the bottom of the screen (the help button does the same)
  Boombox.prototype.liftPill = function () {
    var bar = document.getElementById('PBarNextFrame');
    if (this.pill) this.pill.style.setProperty('--otk-bb-lift', bar && bar.offsetHeight ? '68px' : '0px');
  };

  // Stepping aside for what it must not cover (CLEAR_OF), as the help button does for the page's buttons: an
  // IntersectionObserver whose root is shrunk to the screen's bottom left corner (from the top of the pill and its right
  // end, a little out) reports when one of them moves into that corner or out of it, however it moves; aside() then
  // tests the pill's own box. While one is in the corner it looks again four times a second (the SCROLL cue fades out
  // without moving, which no observer reports). Rebuilt when the screen's size changes (the pill's size changes with it).
  Boombox.prototype.watchAside = function () {
    var self = this, p = this.pill;
    if (!p || this.dead) return;
    if (this.asideIO) this.asideIO.disconnect();
    this.asideIO = null;
    this.things = [].slice.call(document.querySelectorAll(CLEAR_OF));
    this.near = this.things.map(function () { return true; }); // until the observer first reports: test them all
    if (window.IntersectionObserver && this.things.length) {
      var r = p.getBoundingClientRect(), vw = document.documentElement.clientWidth;
      var m = -Math.max(0, Math.floor(r.top - 12)) + 'px ' + -Math.max(0, Math.floor(vw - r.right - 12)) + 'px 0px 0px';
      this.asideIO = new IntersectionObserver(function (en) {
        en.forEach(function (e) {
          var j = self.things.indexOf(e.target);
          if (j >= 0) self.near[j] = e.isIntersecting;
        });
        self.aside();
      }, { rootMargin: m });
      this.things.forEach(function (t) { self.asideIO.observe(t); });
    }
    this.aside();
  };

  Boombox.prototype.aside = function () {
    var p = this.pill, on = false, near = false, pr, j, t, r, cs;
    if (!p || this.dead) return;
    pr = p.getBoundingClientRect();
    for (j = 0; j < this.things.length && !on; j++) {
      if (!this.near[j]) continue;
      near = true;
      t = this.things[j];
      cs = getComputedStyle(t);
      if (cs.visibility === 'hidden' || cs.display === 'none' || parseFloat(cs.opacity) < 0.05) continue;
      r = t.getBoundingClientRect();
      if (r.width && r.right > pr.left && r.left < pr.right && r.bottom > pr.top && r.top < pr.bottom) on = true;
    }
    near = near && this.pillOn; // (nothing to look at while it is hidden anyway)
    if (near && !this.asideT) this.asideT = setInterval(this.aside.bind(this), 250);
    else if (!near && this.asideT) { clearInterval(this.asideT); this.asideT = 0; }
    if (on !== !!this.isAside) {
      this.isAside = on;
      p.classList.toggle('is-aside', on);
    }
  };

  // while the hero is pinned the visitor is in the room (on phones the view may be panned away from the boombox)
  Boombox.prototype.inHero = function () {
    var H = this.hero(), st = H && H.tl && H.tl.scrollTrigger;
    if (!st) return false;
    var y = window.scrollY;
    return y >= st.start - 1 && y <= st.end + 1;
  };

  // the boombox itself can be reached: the door isn't over it, it has room under the sign, and it is on screen (or the
  // visitor is in the room, the hero pinned)
  Boombox.prototype.reachable = function () {
    return this.open && !this.cramped && (this.onScreen || this.inHero());
  };

  // It shows while the music can't be reached on the boombox: it is off screen (and the visitor isn't in the room,
  // i.e. the hero isn't pinned), the door has closed over it again (scrolled back to the top while it plays), or there
  // is no room for it (a phone turned sideways). It never goes from under a keyboard user's focus, unless the boombox
  // is back in reach: the focus then moves onto it.
  Boombox.prototype.syncPill = function () {
    if (this.dead) return;
    var reach = this.reachable();
    var want = (this.playing || this.pillHold) && !this.off && !reach;
    var focused = !!this.pill && this.pill.contains(document.activeElement);
    if (!want && focused && this.pillOn && !reach && !this.off) want = true;
    if (want && !this.pill) this.makePill();
    if (!this.pill) return;
    if (want !== this.pillOn) {
      this.pillOn = want;
      this.pill.classList.toggle('is-shown', want);
      if (want) { this.liftPill(); this.aside(); }
      if (!want && focused && reach) this.btn.focus({ preventScroll: true });
    }
    this.pill.classList.toggle('is-playing', this.playing);
    var pp = this.pillPP, label = this.playing ? 'Pause music' : 'Play music';
    if (pp.getAttribute('aria-label') !== label) pp.setAttribute('aria-label', label);
  };

  Boombox.prototype.hidePill = function () {
    this.pillHold = false;
    clearTimeout(this.holdT);
    this.syncPill();
  };

  /* ------------------------------------------------ its size, and the frame loop: needles, cones, lamp, notes */

  // How big it can be (see the top of otk-boombox.css): its feet stay on the shelf, and its antennas must clear the sign
  // over it by a fifth of its height (at least 14 px). The sign is the title layer's line ("One True King") or the logo
  // the rack engine draws centred on it (8% of the hero's height, 48-80 px: otk-sakura-rack.js), whichever reaches lower.
  // Both are measured in their own layer's box, the pinned hero's (the title layer is a fixed copy of it, moved by the
  // hero script; this layer is the pinned box itself), so the numbers are the same however far the page is scrolled.
  // --bb-fit scales the boombox (down to MIN_FIT); below that there is no room for it (a phone held sideways, where the
  // sign comes down to the shelves) and it steps out of the room (.is-cramped). The cherry trees keep clear of it, so a
  // new size lays them out again.
  Boombox.prototype.fit = function () {
    var H = this.hero(), tb = (H && H.titlebar) || document.querySelector('[data-sakura-titlebar]');
    var title = tb && tb.querySelector('.armory__title');
    if (!title || !this.scene) return;
    var tr = title.getBoundingClientRect(), tbr = tb.getBoundingClientRect(), lr = this.layer.getBoundingClientRect();
    var sr = this.scene.getBoundingClientRect(), br = this.btn.getBoundingClientRect();
    if (!tr.height || !tbr.height || !sr.height || !br.height) return;
    var logo = clamp((this.layer.offsetHeight || window.innerHeight) * 0.08, 48, 80);
    var sign = this.signRel = Math.max(tr.bottom, tr.top + tr.height / 2 + logo / 2) - tbr.top;
    var feet = sr.top - lr.top + sr.height * 0.37677, full = br.height / this.fitV; // (its height at full size)
    var f = clamp((feet - sign - Math.max(14, 0.2 * full)) / full, 0, 1);
    var cramped = f < MIN_FIT;
    f = Math.max(MIN_FIT, Math.round(f * 1000) / 1000);
    // its left edge in frame px, for the door (otk-sakura-hero.js onDoor)
    this.frameLeft = 688 - br.width / this.fitV * f / 2 / (sr.width / 1376);
    if (Math.abs(f - this.fitV) < 0.005 && cramped === this.cramped) return; // (a sub-pixel change: leave it)
    this.fitV = f;
    if (f < 1) this.layer.style.setProperty('--bb-fit', String(f));
    else this.layer.style.removeProperty('--bb-fit');
    if (cramped !== this.cramped) {
      this.cramped = cramped;
      this.layer.classList.toggle('is-cramped', cramped);
      this.syncInert();
      this.syncAwake();
      this.syncPill();
    }
    if (H && H.trees && !H.dead && H.layout) {
      H.layout();
      if (H.placeStatic) H.placeStatic(); // (still trees: move them to the new layout too)
    }
  };

  // sizes and positions on screen (canvas px = CSS px x dpr): the speakers' centres, the case's top edge and the sign's
  // bottom, in the notes canvas's own coordinates (they all move with the room, so these stay put while it pans or
  // scrolls)
  Boombox.prototype.layout = function () {
    var c = this.canvas;
    if (!c || this.dead) return;
    this.fit();
    var cr = c.getBoundingClientRect(), br = this.btn.getBoundingClientRect();
    if (!cr.width || !br.width) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = Math.round(cr.width * dpr), h = Math.round(cr.height * dpr);
    if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    this.ctx2 = this.ctx2 || c.getContext('2d');
    this.dpr = dpr;
    this.cw = cr.width;
    this.ch = cr.height;
    this.nu = br.width / 160;
    var k = br.width / IMG.w, dip = this.layer.classList.contains('is-pressing') ? 14 * k : 0;
    var x0 = br.left - cr.left, y0 = br.top - dip - cr.top;
    this.spk = IMG.speakers.map(function (s) { return { x: x0 + s[0] * k, y: y0 + s[1] * k }; });
    this.caseTop = y0 + IMG.top * k;
    // the notes fade out before they reach the sign (fit() measured its bottom in the pinned box: this layer's)
    var top = this.signRel != null ? this.layer.getBoundingClientRect().top + this.signRel + 6 - cr.top : 0;
    this.fadeTop = clamp(top, 0, this.caseTop - 30 * this.nu);
  };

  Boombox.prototype.wake = function () {
    if (this.dead) return;
    var run = !document.hidden && this.onScreen && !this.viewing && !REDUCED.matches &&
      (this.playing || this.settling || this.notes.length > 0 || this.awake);
    if (run && !this.raf) {
      this.last = 0;
      this.raf = requestAnimationFrame(this.tick);
    } else if (!run && this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  };

  Boombox.prototype.tick = function (now) {
    this.raf = requestAnimationFrame(this.tick);
    var busy = this.playing || this.settling;
    if (!busy && this.last && now - this.last < 30) return; // waiting: 30 frames a second is plenty for drifting notes
    var dt = this.last ? Math.min(0.05, (now - this.last) / 1000) : 1 / 60;
    this.last = now;
    this.levels(dt, now / 1000);
    this.drawNotes(dt);
    if (!this.playing && !this.settling && !this.notes.length && !this.awake) this.wake();
  };

  // RMS of an analyser's waveform (0-1)
  Boombox.prototype.rms = function (a) {
    var s = 0, i, n;
    if (a.getFloatTimeDomainData) {
      a.getFloatTimeDomainData(this.td);
      for (i = 0, n = this.td.length; i < n; i++) s += this.td[i] * this.td[i];
      return Math.sqrt(s / n);
    }
    a.getByteTimeDomainData(this.tdB);
    for (i = 0, n = this.tdB.length; i < n; i++) { var x = (this.tdB[i] - 128) / 128; s += x * x; }
    return Math.sqrt(s / n);
  };

  // The music's levels, or a gentle pulse where they can't be read, and what they drive: the cones pump with the bass
  // and the case thumps a little, a note comes on each beat, the needles swing (VU ballistics: a damped spring, ~300 ms,
  // a touch of overshoot), the lamp (and its light on the wall) follows the level. After a pause everything settles to
  // rest: the needles on their pins, the lamp back to its stand-by glow. While it waits, the notes now and then.
  // The sizes are chosen to be seen at laptop size, where a cone is under 50 px across and the meter 40 px: the cones
  // grow up to 6% on a kick (about 1.5 px), the needles cross most of the scale, the lamp runs from a third to full.
  Boombox.prototype.levels = function (dt, t) {
    var L = this.lv, bass = 0, lv = [0, 0], el = this.cur;
    var live = this.playing && el && !el.paused && el.readyState >= 3; // (sounding: not still loading)
    if (live && this.curWA && this.an) {
      this.an.getByteFrequencyData(this.freq);
      for (var i = this.bass[0]; i <= this.bass[1]; i++) bass += this.freq[i];
      bass /= (this.bass[1] - this.bass[0] + 1) * 255;
      var g = Math.max(0.05, this.vol); // (the needles read the music, not the volume setting)
      lv = [this.rms(this.aLR[0]) / g, this.rms(this.aLR[1]) / g];
      if (lv[1] < 1e-4 && lv[0] > 0.01) lv[1] = lv[0]; // a mono file
    } else if (live) {
      var ph = (el.currentTime * BPM / 60) % 1, beat = Math.exp(-ph * 5);
      bass = 0.35 + 0.45 * beat;
      lv = [VU_REF * (0.75 + 0.35 * beat + 0.08 * Math.sin(t * 7.3)), VU_REF * (0.7 + 0.38 * beat + 0.08 * Math.sin(t * 6.1 + 1))];
    }
    // a VU meter shows the average power over about 300 ms (with the needle's own inertia, below)
    for (var m = 0; m < 2; m++) {
      L.ms[m] += (lv[m] * lv[m] - L.ms[m]) * (1 - Math.exp(-dt / 0.12));
      lv[m] = Math.sqrt(Math.max(0, L.ms[m]));
    }
    // bass envelope: quick up, slower down; its slow average marks the beats
    var up = bass > L.env;
    L.env += (bass - L.env) * (1 - Math.exp(-dt / (up ? 0.025 : 0.13)));
    L.avg += (L.env - L.avg) * (1 - Math.exp(-dt / 0.8));
    L.since += dt;
    var rising = L.env > L.prev;
    L.prev = L.env;
    // the cones: a little for the bass's level, mostly for its kicks (how far it jumps over its recent average)
    var pump = live ? clamp(0.5 * (L.env - 0.35) / 0.4 + 4 * Math.max(0, L.env - L.avg), 0, 1) : 0;
    L.pump += (pump - L.pump) * (1 - Math.exp(-dt / (pump > L.pump ? 0.02 : 0.09)));
    if (live && rising && L.env > L.avg * 1.12 + 0.03 && L.since > 0.28 && this.notes.length < 16) {
      L.since = 0;
      L.side = 1 - L.side;
      this.spawn(L.side, 'beat');
      if (L.env > L.avg * 1.3 + 0.06 && Math.random() < 0.5) this.spawn(1 - L.side, 'beat');
    }
    // a steady stream when the music has no clear beat
    if (live && L.since > 1.4) { L.since = 0.6; L.side = 1 - L.side; this.spawn(L.side, 'play'); }
    if (Math.abs(L.pump - L.pumpW) > 0.003) {
      L.pumpW = L.pump;
      var on = L.pump > 0.003, sc = (1 + 0.06 * L.pump).toFixed(4), sc2 = (1 + 0.05 * L.pump).toFixed(4);
      this.cones.forEach(function (c) { c.style.transform = on ? 'scale(' + sc + ')' : ''; });
      this.caps.forEach(function (c) { c.style.transform = on ? 'scale(' + sc2 + ')' : ''; });
      // the case thumps down a little with each kick (under 1 frame px)
      if (this.thump) this.thump.style.transform = on ? 'translate3d(0,' + (0.8 * L.pump * (this.nu || 1)).toFixed(2) + 'px,0)' : '';
    }
    // the needles: position in proportion to the voltage, as on a real VU scale (+3 VU = 1.413 x the 0 VU level)
    var rest = true;
    for (var n = 0; n < 2; n++) {
      var target = IMG.rest + (IMG.max - IMG.rest) * clamp(lv[n] / VU_REF / 1.413, 0, 1.04);
      var w = 13, z = 0.72, steps = Math.max(1, Math.ceil(dt / 0.008)), h = dt / steps;
      for (var s = 0; s < steps; s++) {
        L.v[n] += (w * w * (target - L.a[n]) - 2 * z * w * L.v[n]) * h;
        L.a[n] += L.v[n] * h;
      }
      if (L.a[n] < IMG.rest) { L.a[n] = IMG.rest; L.v[n] = Math.max(0, L.v[n]); } // the stop pins, at rest ...
      else if (L.a[n] > IMG.max + 2) { L.a[n] = IMG.max + 2; L.v[n] = Math.min(0, L.v[n]); } // ... and just past +3
      if (Math.abs(L.a[n] - IMG.rest) > 0.05 || Math.abs(L.v[n]) > 0.05) rest = false;
      this.setNeedle(n, L.a[n]);
    }
    // the lamp: while it plays it follows the level (a third at -8 VU, full at +3); otherwise its rest (stand-by)
    var restLamp = this.restLamp();
    var lamp = live ? 0.35 + 0.65 * clamp((Math.max(lv[0], lv[1]) / VU_REF - 0.4) / 1.0, 0, 1) : restLamp;
    L.lamp += (lamp - L.lamp) * (1 - Math.exp(-dt / (lamp > L.lamp ? 0.06 : 0.25)));
    var settled = !this.playing && rest && L.pump < 0.003 && Math.abs(L.lamp - restLamp) < 0.004;
    if (settled) L.lamp = restLamp;
    if (Math.abs(L.lamp - L.lampW) > 0.004 || (settled && L.lamp !== L.lampW)) { L.lampW = L.lamp; this.writeLamp(L.lamp); }
    if (settled) {
      this.settling = false;
      if (L.pumpW !== 0) {
        this.cones.concat(this.caps).forEach(function (c) { c.style.transform = ''; });
        if (this.thump) this.thump.style.transform = '';
        L.pumpW = 0;
      }
    }
    // waiting to be pressed: a hello of three notes once it wakes, then one now and then
    if (!this.playing && this.awake) {
      if (this.hello > 0 && (this.hello -= dt) <= 0) { this.helloN = 3; this.helloGap = 0; }
      if (this.helloN > 0 && (this.helloGap -= dt) <= 0) {
        this.helloN--;
        this.helloGap = 0.24;
        this.spawn(this.helloN % 2, 'hello');
      }
      this.nextIdle -= dt;
      if (this.nextIdle <= 0) {
        this.nextIdle = 1.8 + Math.random() * 1.4;
        this.spawn((Math.random() * 2) | 0, 'idle');
      }
    }
  };

  Boombox.prototype.setNeedle = function (n, a) {
    var el = this.needleEls[n], L = this.lv;
    if (!el) return;
    var v = Math.round(a * 10) / 10;
    if (v === L.aW[n]) return;
    L.aW[n] = v;
    var p = IMG.pivots[n];
    el.setAttribute('transform', 'rotate(' + v + ' ' + p[0] + ' ' + p[1] + ')');
  };

  // a note out of speaker i (0 left, 1 right): it rises out of the top of the case above that speaker (it comes into
  // sight as it clears the case's top edge, so it is never painted across the boombox's face), grows as it leaves,
  // drifts outward and sways, turning a little, and fades before it reaches the sign. kind: 'beat' / 'play' (playing:
  // livelier and brighter), 'hello' (waking up: bigger), 'idle' (waiting: smaller, softer)
  Boombox.prototype.spawn = function (i, kind) {
    if (!this.spk || !noteSprites() || this.viewing) return;
    var s = this.spk[i], u = this.nu || 1, out = i ? 1 : -1, play = kind === 'beat' || kind === 'play';
    var size = kind === 'hello' ? 17 + Math.random() * 4 : play ? (kind === 'beat' ? 15 : 14) + Math.random() * 6 : 14 + Math.random() * 4;
    this.notes.push({
      x: s.x + (Math.random() - 0.5) * 16 * u,
      y: this.caseTop + 4 * u,
      vx: out * (3 + Math.random() * 8) * u,
      vy: -(play ? 30 + Math.random() * 16 : 22 + Math.random() * 9) * u,
      sway: (2.5 + Math.random() * 4) * u,
      ph: Math.random() * 6.283,
      sw: 1.4 + Math.random() * 1.4,
      r0: (Math.random() - 0.5) * 0.5 + out * 0.08,
      vr: out * (0.05 + Math.random() * 0.22) * (play ? 1.5 : 1),
      size: size * u,
      a: play ? 0.95 : kind === 'hello' ? 0.9 : 0.75,
      sp: sprites[(Math.random() * sprites.length) | 0],
      age: 0
    });
  };

  Boombox.prototype.drawNotes = function (dt) {
    var g = this.ctx2, N = this.notes;
    if (!g) return;
    if (!N.length) {
      if (this.drawn) { g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, this.canvas.width, this.canvas.height); this.drawn = false; }
      return;
    }
    var dpr = this.dpr, u = this.nu || 1, top = this.fadeTop || 0, half = SPRITE / 2, edge = this.caseTop;
    // the fade before the sign: over up to 34 frame px, less where the sign is close
    var zone = clamp((edge - 10 * u - top) * 0.45, 12 * u, 34 * u);
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, this.canvas.width, this.canvas.height);
    for (var j = N.length - 1; j >= 0; j--) {
      var p = N[j];
      p.age += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy *= 1 - 0.18 * dt; // slows a little as it rises
      var x = p.x + Math.sin(p.ph + p.age * p.sw) * p.sway;
      var a = p.a * Math.min(1, p.age / 0.2) * smooth(edge + 2 * u, edge - 8 * u, p.y) * smooth(top, top + zone, p.y);
      if (p.y < top || p.age > 6 || (a <= 0.002 && p.age > 1.2)) { N[j] = N[N.length - 1]; N.pop(); continue; }
      if (a <= 0.002) continue; // (still behind the case's top edge)
      var grow = 0.35 + 0.65 * smooth(0, 0.55, p.age);
      var rot = p.r0 + Math.sin(p.age * 1.6 + p.ph) * 0.2 + p.vr * p.age;
      var sc = p.size * grow / (SPRITE * 0.62) * dpr, cs = Math.cos(rot) * sc, sn = Math.sin(rot) * sc;
      g.globalAlpha = a;
      g.setTransform(cs, sn, -sn, cs, x * dpr, p.y * dpr);
      g.drawImage(p.sp, -half, -half);
    }
    g.globalAlpha = 1;
    this.drawn = true;
  };

  Boombox.prototype.destroy = function () {
    this.dead = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    [this.layoutT, this.pauseT, this.switchT, this.capT, this.holdT].forEach(clearTimeout);
    clearInterval(this.volT);
    clearInterval(this.asideT);
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    [this.mo, this.moSection, this.moHtml, this.io, this.asideIO].forEach(function (o) { if (o) o.disconnect(); });
    if (this.motion) {
      if (REDUCED.removeEventListener) REDUCED.removeEventListener('change', this.motion);
      else if (REDUCED.removeListener) REDUCED.removeListener(this.motion);
    }
    [this.el, this.plain].forEach(function (el) { if (el) { el.pause(); el.removeAttribute('src'); try { el.load(); } catch (e) { /* */ } } });
    if (this.ctx && this.ctx.close) this.ctx.close().catch(noop);
    if (this.pill && this.pill.parentNode) this.pill.parentNode.removeChild(this.pill);
    if (this.live && this.live.parentNode) this.live.parentNode.removeChild(this.live);
    if ('mediaSession' in navigator && this.el) {
      ['play', 'pause', 'nexttrack'].forEach(function (a) { try { navigator.mediaSession.setActionHandler(a, null); } catch (e) { /* */ } });
    }
    // back to the markup's state (the theme editor may boot the same section again)
    this.layer.classList.remove('is-ready', 'is-awake', 'is-playing', 'is-pressing', 'is-off', 'is-cramped');
    this.layer.style.removeProperty('--bb-fit');
    [this.vu, this.bloom, this.thump].concat(this.cones, this.caps).forEach(function (e) { if (e) { e.style.opacity = ''; e.style.transform = ''; } });
    if (this.btn) {
      this.btn.setAttribute('aria-pressed', 'false');
      this.btn.setAttribute('aria-label', 'Play music');
      this.btn.removeAttribute('aria-disabled');
    }
    if (this.nextBtn) this.nextBtn.hidden = true;
    if (this.caption) this.caption.classList.remove('is-on');
    this.layer._otkBoombox = null;
  };

  function boot(section) {
    var layer = section && section.querySelector('[data-otk-boombox]');
    if (!layer || layer._otkBoombox) return;
    try {
      layer._otkBoombox = new Boombox(layer, section);
    } catch (err) {
      layer._otkBoombox = null; // it stays a still picture on its shelf
    }
  }

  function init() {
    document.querySelectorAll('[data-otk-sakura]').forEach(boot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // theme editor (registered after the hero's own handlers: it has booted, or been torn down, first)
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target.querySelector('[data-otk-sakura]');
    if (s) boot(s);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var l = e.target.querySelector('[data-otk-boombox]');
    if (l && l._otkBoombox) l._otkBoombox.destroy();
  });
})();
